import { Accordion, AccordionActions, AccordionDetails, AccordionSummary, Box, CircularProgress, Typography } from "@mui/material";
import { EditIcon, ExitIcon, RestoreIcon, SaveIcon } from "../../../../../common/icons/Icons";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { fadeInMixin } from "../../../../../common/cssMixins";
import { CustomBackdrop } from "../../../../../common/LoadingScreen";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useFormValidator } from "../../../../../common/inputs/useFormValidator";
import { useEditableEntity } from "../../../../../common/hooks/useEditableEntity";
import PropTypes from 'prop-types';

const accordionStyle = {
    margin: '0px',
    '&.Mui-expanded': {
        margin: '0px !important',
    },
    '&:before': {
        display: 'none'
    },
    '& .MuiAccordionSummary-root.editing': {
        pointerEvents: 'none',
        '& .MuiAccordionSummary-expandIconWrapper': {
            display: 'none',
        },
    },
    height: 'fit-content',
    ...fadeInMixin(),
}

const ENUM_LOADING_STATUS = {
    SAVING: 'Saving...',
}

const EditButtons = (props) => {
    const { modified, onSave, onReset, onCancel, valid } = props;

    return <Box sx={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', alignItems: 'center', flexBasis: '150px', flexGrow: 1, maxWidth: '180px' }}>
        {modified && valid && <SaveIcon onClick={onSave} />}
        {modified && <RestoreIcon onClick={onReset} />}
        {!modified && <ExitIcon onClick={onCancel} title='Cancel editing' />}
    </Box>
}

const ReservationSection = ({ sectionId, title, disabled, editable, editing, onEdit, onSave, onEditCancel, onError, onReset, InnerComponent, reservation, innerConponentProps, queryConfigToFetchOnEdit, ...rest }) => {
    const [loadingStatus, setLoadingStatus] = useState(null);

    const [childrenChanged, setChildrenChanged] = useState(false);//this field indicates if anything apart from reservation definition has been changed in the child components and whether save button should be enabled
    const [editableReservation, setEditableReservation, reservationChanged, resetEditableReservation] = useEditableEntity(reservation);
    const [reservationValid, setReservationValid, resetValid, fieldsValidity] = useFormValidator();    // eslint-disable-line no-unused-vars

    const childRef = useRef();

    const handleEdit = () => {
        resetEditableReservation();
        onEdit(sectionId);
    }

    const onChange = useCallback(async (idOrArray, value, valid = true) => {
        if (Array.isArray(idOrArray)) {
            const elements = idOrArray;

            let updatedReservation = {
                ...editableReservation,
            }

            for (const element of elements) {
                const { id, value, valid = true } = element;

                setReservationValid(id, valid);

                if (!editableReservation.hasOwnProperty(id)) {
                    // Child-managed field (not part of reservation payload); undefined value means validate-only
                    if (value !== undefined) {
                        setChildrenChanged(true);
                    }
                }
                else if (editableReservation[id] !== value) {
                    updatedReservation[id] = value;
                }
            }
            setEditableReservation(updatedReservation);
        }
        else {
            const id = idOrArray;

            setReservationValid(id, valid);

            if (!editableReservation.hasOwnProperty(id)) {
                if (value !== undefined) {
                    setChildrenChanged(true);
                }
            }
            else if (editableReservation[id] !== value) {
                let updatedReservation = {
                    ...editableReservation,
                }

                updatedReservation[id] = value;

                setEditableReservation(updatedReservation);
            }
        }
    }, [editableReservation, setEditableReservation, setReservationValid]);

    //this is to re-validate the children whenever reservation changes
    useEffect(() => {
        reservationChanged && childRef.current && childRef.current.validate && childRef.current.validate();
    }, [reservationChanged, editableReservation]);

    const handleSave = useCallback(async () => {
        setLoadingStatus(ENUM_LOADING_STATUS.SAVING);

        const additionalActions = [];
        if (childrenChanged && childRef.current && childRef.current.save) {
            additionalActions.push(childRef.current.save);//trigger save action in any children if they have it
        }

        try {
            await onSave(editableReservation, reservationChanged, additionalActions);//save the reservation definition
            setChildrenChanged(false);
        }
        catch (error) {
            onError(error);
        }

        setLoadingStatus(null);
    }, [editableReservation, reservationChanged, childrenChanged, onSave, onError]);

    const loading = useMemo(() => loadingStatus !== null, [loadingStatus]);

    const handleReset = useCallback(() => {
        setEditableReservation(reservation);
        if (childRef.current?.reset) {
            childRef.current.reset();
            setChildrenChanged(false);
            resetValid();
        }
    }, [reservation, setEditableReservation, resetValid]);

    const setEditing = useCallback((editing) => {
        editing ? onEdit() : onEditCancel();
    }, [onEdit, onEditCancel]);

    const regularActions = <EditIcon onClick={handleEdit} loading={loading} />;

    const editingActions = useMemo(() => <Box sx={{ display: 'flex', gap: '20px', width: '-webkit-fill-available', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', gap: '10px', alignItems: 'center' }}>{loadingStatus && <><CircularProgress size={'20px'} /><Typography>{loadingStatus}</Typography></>}</Box>
        <EditButtons modified={reservationChanged || childrenChanged} onSave={handleSave} onCancel={onEditCancel} onReset={handleReset} valid={reservationValid} />
    </Box>, [reservationChanged, childrenChanged, onEditCancel, handleReset, loadingStatus, reservationValid, handleSave]);

    return <Accordion
        defaultExpanded={true}
        sx={{
            ...accordionStyle,
            zIndex: disabled ? 'unset' : 1,
        }}
        square
        disableGutters
        {...rest}
    >
        <AccordionSummary className={editing ? 'editing' : ''} expandIcon={<ExpandMoreIcon sx={{ color: 'primary.contrastText' }} />} sx={{ backgroundColor: 'primary.main', color: 'primary.contrastText' }}>
            <Typography sx={{ fontWeight: "bold" }}>{`${editing ? 'Editing: ' : ''}${title}`}</Typography>
        </AccordionSummary>
        <Box sx={{ position: 'relative' }}>
            {loading && <CustomBackdrop />}
            {editable && <AccordionActions sx={{ minHeight: '40px', padding: '8px 16px', justifyContent: editing ? 'space-between' : 'flex-end' }}>{editing ? editingActions : regularActions}</AccordionActions>}
            <AccordionDetails sx={{ position: 'relative', minHeight: 150, maxHeight: editing ? '2000px' : '1000px', padding: '16px' }}>
                <InnerComponent
                    editing={editing}
                    reservation={editing ? editableReservation : reservation}
                    originalReservation={reservation}
                    onChange={onChange}
                    onError={onError}
                    setLoadingStatus={setLoadingStatus}
                    setEditing={setEditing}
                    ref={childRef}
                    resetChildrenChanged={() => setChildrenChanged(false)}
                    {...innerConponentProps}
                />
            </AccordionDetails>
        </Box>
    </Accordion>
}

ReservationSection.propTypes = {
    sectionId: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    disabled: PropTypes.bool,
    editable: PropTypes.bool,
    editing: PropTypes.bool,
    onEdit: PropTypes.func,
    onSave: PropTypes.func,
    onEditCancel: PropTypes.func,
    onError: PropTypes.func.isRequired,
    InnerComponent: PropTypes.elementType.isRequired,
};

// Default prop for onEditCancel if not provided when editable is true
ReservationSection.defaultProps = {
    onEditCancel: (props) => {
        if (props.editable) {
            console.error("onEditCancel is required when editable is true");
        }
    },
    onEdit: (props) => {
        if (props.editable) {
            console.error("onEdit is required when editable is true");
        }
    },
    onSave: (props) => {
        if (props.editable) {
            console.error("onSave is required when editable is true");
        }
    },
};


export default ReservationSection;